import React, { Component, Fragment } from 'react';
import { TABLET_BREAKPOINT, DESKTOP_BREAKPOINT } from 'constants/ads/sizes';
import { SITE_KEY } from 'constants/site';
import { findClosestNode } from 'util/dom';
import { SIZE_CONFIG, getAdUnitConfig } from './config';

/*
  Prebid must be built with adapters/modules:
  - AppNexus
  - Index Exchange
  - Triple Lift
  - Rubicon
  - Teads
  - DFP Video
  - Pubmatic

  This can be done at https://docs.prebid.org/download.html or by cloning
  https://github.com/prebid/Prebid.js and running:
  gulp build --modules=appnexusBidAdapter,tripleliftBidAdapter,ixBidAdapter,rubiconBidAdapter,dfpAdServerVideo,teadsBidAdapter,pubmaticBidAdapter
*/
const PREBID_URL = '/static/prebid.4.22.0.js';
const PREBID_TIMEOUT = 2000;
const PRICE_GRANULARITY = {
  buckets: [
    { precision: 2, max: 3, increment: 0.01 },
    { precision: 2, max: 8, increment: 0.05 },
    { precision: 2, max: 20, increment: 0.5 },
    { precision: 2, max: 45, increment: 1 }
  ]
};

class Prebid extends Component {
  static isPbjsAd(code) {
    return (
      window.pbjs &&
      window.pbjs.adUnits &&
      window.pbjs.adUnits.some(u => u.code === code)
    );
  }

  // Checks to see if the wrapping ad has a data-size-label that matches the
  // current viewport. This is used to prevent bids being sent on ad units that
  // do not have any sizes configured for the current viewport.
  // CLEANUP: Move to BaseAd
  static hasSizeForViewport(code) {
    // Check if a bid should be fetched for this viewport
    const div = document.getElementById(code);
    const wrapper = div ? findClosestNode(div, '.Ad') : undefined;

    if (!wrapper) {
      return true;
    }

    const sizeLabel = wrapper.getAttribute('data-size-label');
    const { innerWidth } = window;

    switch (sizeLabel) {
      case 'post-small':
        return innerWidth < DESKTOP_BREAKPOINT;
      case 'post-large':
        return innerWidth >= DESKTOP_BREAKPOINT;
      case 'mobile':
        return innerWidth < TABLET_BREAKPOINT;
      case 'tablet-desktop':
        return innerWidth >= TABLET_BREAKPOINT;
      case 'desktop':
        return innerWidth >= DESKTOP_BREAKPOINT;
      default:
        return true;
    }
  }

  static fetchBannerBids(slots) {
    // Assumes slots have a matching PBJS configuration!
    const codes = slots.map(slot => slot.getSlotElementId());
    return new Promise(resolve => {
      window.pbjs.requestBids({
        adUnitCodes: codes,
        bidsBackHandler: () => {
          if (codes.length) {
            window.googletag.cmd.push(() => {
              window.pbjs.setTargetingForGPTAsync(codes);
              resolve(slots);
            });
          }
        }
      });
    });
  }
  static fetchVideoBid(code, custParams = {}) {
    // Assumes code has a matching PBJS configuration!
    return new Promise(resolve => {
      window.pbjs.requestBids({
        adUnitCodes: [code],
        bidsBackHandler: () => {
          // Get the pbjs unit
          const pbjsVideoAdUnit = window.pbjs.adUnits.find(
            adUnit => adUnit.code === code
          );
          // Build params
          const params = {
            adUnit: pbjsVideoAdUnit,
            params: {
              iu: pbjsVideoAdUnit.mediaTypes.video.code,
              cust_params: custParams
            }
          };
          // Return complete ad tag
          resolve(window.pbjs.adServers.dfp.buildVideoUrl(params));
        }
      });
    });
  }
  static init() {
    // TEMP: Remove when PBJS is enabled for Cubby
    if (SITE_KEY === 'cb') {
      return Promise.resolve();
    }
    // If the Prebid script takes too long to load, resolve so that we can render
    // ads without pbjs
    const _timeout = new Promise(resolve => {
      window.setTimeout(() => {
        resolve();
      }, PREBID_TIMEOUT);
    });
    // Set config and add ad units
    const _init = new Promise(resolve => {
      // Make sure pbjs que exists
      window.pbjs = window.pbjs || {};
      window.pbjs.que = window.pbjs.que || [];

      // Make sure googletag.cmd exists
      window.googletag = window.googletag || {};
      window.googletag.cmd = window.googletag.cmd || [];

      // Push to queue...
      window.pbjs.que.push(() => {
        window.pbjs.setConfig({
          useBidCache: true,
          sizeConfig: SIZE_CONFIG,
          priceGranularity: PRICE_GRANULARITY,
          // Use cache for video bidding (see Prebid docs)
          cache: { url: 'https://prebid.adnxs.com/pbc/v1/cache' }
        });
        // Uncomment to set bid adjustments, etc
        // window.pbjs.bidderSettings = {};
        // Add ad units
        window.pbjs.addAdUnits(getAdUnitConfig());
        resolve();
      });
    });
    return Promise.race([_init, _timeout]);
  }
  static Head() {
    // TEMP: Remove when PBJS is enabled for Cubby
    if (SITE_KEY === 'cb') {
      return null;
    }
    return (
      <Fragment>
        <script key="pbjs-1" defer src={PREBID_URL} />
      </Fragment>
    );
  }
  render() {
    return null;
  }
}

export default Prebid;
